import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Plus, Trash2, Loader2, Edit2 } from 'lucide-react';
import Cookies from 'js-cookie';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Country() {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCountry, setNewCountry] = useState({ name: '', country_code: '', flag: '' });
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ name: '', country_code: '', flag: '' });
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      const token = Cookies.get('admin_token');
      const response = await axios.get(`${API}/master/countries`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCountries(response.data.data || []);
    } catch (err) {
      toast.error('Failed to load countries');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newCountry.name.trim() || !newCountry.country_code.trim()) return;

    setAdding(true);
    try {
      const token = Cookies.get('admin_token');
      const submitData = {
        name: newCountry.name,
        country_code: newCountry.country_code,
        flag: newCountry.flag || undefined
      };
      await axios.post(`${API}/master/countries`, 
        submitData,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success('Country added successfully');
      setNewCountry({ name: '', country_code: '', flag: '' });
      setOpen(false);
      fetchCountries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add country');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this country?')) return;

    try {
      const token = Cookies.get('admin_token');
      await axios.delete(`${API}/master/countries/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Country deleted');
      fetchCountries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete country');
    }
  };

  const handleEdit = (country) => {
    setEditingId(country.id);
    setEditData({ 
      name: country.name || '', 
      country_code: country.country_code || '', 
      flag: country.flag || '' 
    });
    setEditOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editData.name || !editData.name.trim() || !editData.country_code || !editData.country_code.trim()) return;

    try {
      const token = Cookies.get('admin_token');
      const submitData = {
        name: editData.name,
        country_code: editData.country_code
      };
      // Only include flag if it has a value
      if (editData.flag && editData.flag.trim()) {
        submitData.flag = editData.flag;
      }
      await axios.put(`${API}/master/countries/${editingId}`, submitData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Country updated successfully');
      setEditOpen(false);
      setEditingId(null);
      fetchCountries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update country');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading mb-2">Country</h1>
          <p className="text-muted-foreground">Manage countries in the system</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="editorial">
              <Plus className="h-4 w-4 mr-2" /> Add Country
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Country</DialogTitle>
              <DialogDescription>Enter country name to add to the system</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAdd}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="country">Country Name *</Label>
                  <Input
                    id="country"
                    value={newCountry.name}
                    onChange={(e) => setNewCountry({...newCountry, name: e.target.value})}
                    placeholder="Enter country name"
                    disabled={adding}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country_code">Country Code *</Label>
                  <Input
                    id="country_code"
                    value={newCountry.country_code}
                    onChange={(e) => setNewCountry({...newCountry, country_code: e.target.value})}
                    placeholder="e.g., +91, +1, +44"
                    disabled={adding}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="flag">Country Flag (Optional)</Label>
                  <Input
                    id="flag"
                    value={newCountry.flag}
                    onChange={(e) => setNewCountry({...newCountry, flag: e.target.value})}
                    placeholder="Flag emoji (e.g., 🇮🇳, 🇺🇸)"
                    disabled={adding}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={adding}>
                  Cancel
                </Button>
                <Button type="submit" disabled={adding || !newCountry.name.trim() || !newCountry.country_code.trim()}>
                  {adding ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Adding...</>
                  ) : (
                    'Add Country'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit Country Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Country</DialogTitle>
            <DialogDescription>Update country information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-country">Country Name *</Label>
                <Input
                  id="edit-country"
                  value={editData.name}
                  onChange={(e) => setEditData({...editData, name: e.target.value})}
                  placeholder="Enter country name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-country_code">Country Code *</Label>
                <Input
                  id="edit-country_code"
                  value={editData.country_code}
                  onChange={(e) => setEditData({...editData, country_code: e.target.value})}
                  placeholder="e.g., +91, +1, +44"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-flag">Country Flag (Optional)</Label>
                <Input
                  id="edit-flag"
                  value={editData.flag}
                  onChange={(e) => setEditData({...editData, flag: e.target.value})}
                  placeholder="Flag emoji (e.g., 🇮🇳, 🇺🇸)"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!editData.name.trim() || !editData.country_code.trim()}>
                Update Country
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Countries List */}
      <Card>
        <CardHeader>
          <CardTitle>All Countries ({countries.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            </div>
          ) : countries.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No countries added yet</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {countries.map((country) => (
                <div
                  key={country.id}
                  className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    {country.flag && <span className="text-2xl">{country.flag}</span>}
                    <div>
                      <p className="font-medium">{country.name}</p>
                      <p className="text-sm text-muted-foreground">{country.country_code}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(country)}
                    >
                      <Edit2 className="h-4 w-4 text-primary" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(country.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
